import React from 'react'
import {
    App,
    Button,
    Empty,
    Modal,
    Select,
    Space,
    Spin,
    Typography,
} from 'antd'
import { useTranslation } from 'react-i18next'

import {
    getAnnotationModelConfig,
    updateAnnotationModelConfig,
} from '../../services/historyService'
import type {
    HistoryAnnotationModelConfig,
    HistoryAnnotationModelOption,
} from '../../types/history'

const { Text } = Typography

interface AnnotationModelSettingsModalProps {
    open: boolean
    onClose: () => void
    mode?: 'settings' | 'confirmation'
    onSaved?: (config: HistoryAnnotationModelConfig) => void
}

const MODEL_KEY_PRIORITY: Record<string, number> = { mosquito: 0, insect: 1 }
const ANNOTATION_MODEL_KEYS = ['mosquito', 'insect'] as const
const NOT_USED_OPTION_PREFIX = 'not-used:'

const groupModelOptions = (
    options: HistoryAnnotationModelOption[],
) => {
    const groups = new Map<string, HistoryAnnotationModelOption[]>(
        ANNOTATION_MODEL_KEYS.map((modelKey) => [modelKey, []]),
    )
    for (const option of options) {
        const group = groups.get(option.model_key) ?? []
        group.push(option)
        groups.set(option.model_key, group)
    }
    return Array.from(groups).sort(
        ([leftModelKey], [rightModelKey]) =>
            (MODEL_KEY_PRIORITY[leftModelKey] ?? 99) -
                (MODEL_KEY_PRIORITY[rightModelKey] ?? 99) ||
            leftModelKey.localeCompare(rightModelKey),
    )
}

export const AnnotationModelSettingsModal: React.FC<
    AnnotationModelSettingsModalProps
> = ({ open, onClose, mode = 'settings', onSaved }) => {
    const { t } = useTranslation(['history', 'admin'])
    const { message } = App.useApp()
    const [config, setConfig] =
        React.useState<HistoryAnnotationModelConfig | null>(null)
    const [selectionByModelKey, setSelectionByModelKey] = React.useState<
        Record<string, string>
    >({})
    const [loading, setLoading] = React.useState(false)
    const [saving, setSaving] = React.useState(false)
    const [loadFailed, setLoadFailed] = React.useState(false)
    const requestIdRef = React.useRef(0)

    const loadConfig = React.useCallback(async () => {
        const requestId = ++requestIdRef.current
        setLoading(true)
        setLoadFailed(false)
        const nextConfig = await getAnnotationModelConfig()
        if (requestId !== requestIdRef.current) {
            return
        }
        setConfig(nextConfig)
        setSelectionByModelKey(() => {
            if (!nextConfig || mode === 'confirmation') {
                return {}
            }
            const selectedByModelKey = Object.fromEntries(
                nextConfig.selected_model_option_keys.flatMap((optionKey) => {
                    const option = nextConfig.model_options.find(
                        (candidate) => candidate.option_key === optionKey,
                    )
                    return option ? [[option.model_key, optionKey]] : []
                }),
            )
            return Object.fromEntries(
                ANNOTATION_MODEL_KEYS.map((modelKey) => [
                    modelKey,
                    selectedByModelKey[modelKey] ??
                        `${NOT_USED_OPTION_PREFIX}${modelKey}`,
                ]),
            )
        })
        setLoadFailed(nextConfig === null)
        setLoading(false)
    }, [mode])

    React.useEffect(() => {
        if (!open) {
            return
        }
        void loadConfig()
        return () => {
            requestIdRef.current += 1
        }
    }, [loadConfig, open])

    const handleModelOptionChange = (modelKey: string, optionKey?: string) => {
        setSelectionByModelKey((current) => {
            if (!optionKey) {
                const next = { ...current }
                delete next[modelKey]
                return next
            }
            return { ...current, [modelKey]: optionKey }
        })
    }

    const modelOptionGroups = groupModelOptions(config?.model_options ?? [])
    const selectedOptionKeys = modelOptionGroups.flatMap(([modelKey]) => {
        const optionKey = selectionByModelKey[modelKey]
        return optionKey && !optionKey.startsWith(NOT_USED_OPTION_PREFIX)
            ? [optionKey]
            : []
    })
    const allModelRolesConfirmed = modelOptionGroups.every(([modelKey]) =>
        Object.prototype.hasOwnProperty.call(selectionByModelKey, modelKey),
    )
    const canSubmitSelection =
        mode === 'confirmation'
            ? allModelRolesConfirmed
            : config !== null

    const handleSave = async () => {
        if (!canSubmitSelection) {
            return
        }
        setSaving(true)
        const updatedConfig = await updateAnnotationModelConfig(
            selectedOptionKeys,
            config?.latest_model_version_ids,
        )
        setSaving(false)
        if (updatedConfig === null) {
            message.error(t('messages.annotationModelConfigSaveFailed'))
            return
        }
        setConfig(updatedConfig)
        message.success(
            t(
                mode === 'confirmation'
                    ? 'messages.annotationModelConfirmationSaved'
                    : 'messages.annotationModelConfigSaved',
            ),
        )
        onSaved?.(updatedConfig)
        onClose()
    }

    const pendingOptionKeys = new Set(config?.pending_model_option_keys ?? [])
    const currentOptionKeys = new Set(config?.selected_model_option_keys ?? [])
    const modelLabel = (modelKey: string) =>
        t(`admin:modelManagement.modelKeys.${modelKey}`, {
            defaultValue: modelKey,
        })
    const optionLabel = (option: HistoryAnnotationModelOption) => {
        const status = pendingOptionKeys.has(option.option_key)
            ? t('detail.annotationModelOptionNew')
            : currentOptionKeys.has(option.option_key)
              ? t('detail.annotationModelOptionCurrent')
              : ''
        return t('detail.annotationModelOptionLabel', {
            model: modelLabel(option.model_key),
            version:
                option.version_label ||
                t('detail.annotationModelFallbackVersion'),
            status,
        })
    }

    return (
        <Modal
            title={t(
                mode === 'confirmation'
                    ? 'modal.annotationModelConfirmationTitle'
                    : 'modal.annotationModelSettingsTitle',
            )}
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            okText={t(
                mode === 'confirmation'
                    ? 'actions.confirmAnnotationModelSettings'
                    : 'actions.saveAnnotationModelSettings',
            )}
            cancelText={t(
                mode === 'confirmation'
                    ? 'actions.confirmAnnotationModelLater'
                    : 'actions.cancel',
            )}
            confirmLoading={saving}
            okButtonProps={{
                disabled: loading || !canSubmitSelection,
            }}
            destroyOnClose
        >
            {loading ? (
                <div style={{ padding: 32, textAlign: 'center' }}>
                    <Spin />
                </div>
            ) : loadFailed ? (
                <Space direction="vertical" size={8}>
                    <Text type="danger">
                        {t('messages.annotationModelConfigLoadFailed')}
                    </Text>
                    <Button onClick={() => void loadConfig()}>
                        {t('actions.refresh')}
                    </Button>
                </Space>
            ) : (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Text type="secondary">
                        {t(
                            mode === 'confirmation'
                                ? 'detail.annotationModelConfirmationHint'
                                : 'detail.annotationModelSettingsHint',
                        )}
                    </Text>
                    {modelOptionGroups.length === 0 ? (
                        <Empty
                            description={t(
                                'messages.annotationModelUnavailable',
                            )}
                        />
                    ) : (
                        modelOptionGroups.map(([modelKey, options]) => (
                            <Space
                                key={modelKey}
                                direction="vertical"
                                size={4}
                                style={{ width: '100%' }}
                            >
                                <Text>{modelLabel(modelKey)}</Text>
                                <Select
                                    allowClear={mode === 'settings'}
                                    aria-label={modelLabel(modelKey)}
                                    placeholder={t(
                                        'detail.annotationModelSelectPlaceholder',
                                        { model: modelLabel(modelKey) },
                                    )}
                                    value={selectionByModelKey[modelKey]}
                                    onChange={(optionKey) =>
                                        handleModelOptionChange(
                                            modelKey,
                                            optionKey,
                                        )
                                    }
                                    options={[
                                        {
                                            value: `${NOT_USED_OPTION_PREFIX}${modelKey}`,
                                            label: t(
                                                'detail.annotationModelNotUsed',
                                                {
                                                    model: modelLabel(modelKey),
                                                },
                                            ),
                                        },
                                        ...[...options]
                                            .sort((left, right) => {
                                                const leftPending =
                                                    pendingOptionKeys.has(
                                                        left.option_key,
                                                    )
                                                const rightPending =
                                                    pendingOptionKeys.has(
                                                        right.option_key,
                                                    )
                                                if (
                                                    leftPending !== rightPending
                                                ) {
                                                    return leftPending ? -1 : 1
                                                }
                                                return (
                                                    (right.model_version_id ??
                                                        0) -
                                                    (left.model_version_id ?? 0)
                                                )
                                            })
                                            .map((option) => ({
                                                value: option.option_key,
                                                label: optionLabel(option),
                                            })),
                                    ]}
                                    style={{ width: '100%' }}
                                />
                            </Space>
                        ))
                    )}
                </Space>
            )}
        </Modal>
    )
}
